<template>
  <v-content>
    <v-dialog
      v-if="noEncryptedIdError"
      :value="true"
      persistent
      hide-overlay
      width="50%"
      lazy
    >
      <v-card
        color="error"
        dark
      >
        <v-card-text class="white--text">
          {{ $t('general.errorTryAgain') }}
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="diagramDataLoaded && noSeriesData"
      :value="true"
      persistent
      hide-overlay
      width="50%"
      lazy
    >
      <v-card
        color="warning"
        dark
      >
        <v-card-text class="white--text">
          {{ $t('download.noDataForSelectedPeriod') }}
        </v-card-text>
      </v-card>
    </v-dialog>
    <highcharts
      ref="myChart"
      :options="chartOptions"
      :callback="chartReady"
    ></highcharts>
    <v-btn
      v-if="storeUser && storeUser.isProfessional"
      flat
      :disabled="!diagramDataLoaded || period === 7"
      color="primary"
      small
      @click="getDataByDirection('back')"
    >
      <v-icon left dark>keyboard_arrow_left</v-icon>
      {{ $t('general.back') }}
    </v-btn>
    <v-btn
      v-if="storeUser && storeUser.isProfessional"
      flat
      :disabled="!diagramDataLoaded || period === 7"
      color="primary"
      small
      @click="getDataByDirection('original')"
    >
      {{ $t('general.clear') }}
    </v-btn>
    <v-btn
      v-if="storeUser && storeUser.isProfessional"
      flat
      :disabled="!diagramDataLoaded || period === 7 || direction >= 0"
      color="primary"
      small
      @click="getDataByDirection('forward')"
    >
      {{ $t('general.weiter') }}
      <v-icon right dark>keyboard_arrow_right</v-icon>
    </v-btn>
    <v-btn
      v-if="!noEncryptedIdError"
      flat
      color="primary"
      small
      @click="toggleAllSeries"
    >
      {{ $t('general.toggle') }}
    </v-btn>
  </v-content>
</template>

<script>
import moment from 'moment'
import momentTz from 'moment-timezone'
import { mapGetters } from 'vuex'
import Units from '../../lib/units'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'

let CACHE = {}

export default {
  props: {
    idDiagram: {
      type: null
    },
    showDiagram: {
      type: null
    },
    customChartOptions: {
      type: Object
    }
  },
  components: {
    highcharts: Chart
  },
  mounted () {
    this.checkPublicLang()

    window.moment = moment
    this.getInterval()
    this.init()
    if (this.showDiagram || this.showDiagram === undefined) {
      this.loadDiagram()
    }
    if (this.customChartOptions && this.customChartOptions.dashboardMode) {
      this.dashboardMode = true
    } else {
      window.addEventListener('resize', this.windowResized)
    }
  },
  beforeDestroy () {
    clearInterval(this.newDataInterval)
    window.removeEventListener('resize', this.windowResized)
    CACHE = {}
  },
  data () {
    return {
      id: null,
      chart: null,
      errors: [],
      diagram: null,
      yAxisOrig: [],
      chartOptions: {},
      timezone: null,
      timezoneOffset: 0,
      newDataInterval: null,
      encryptedId: null,
      options: null,
      series: [],
      seriesCached: null,
      dashboardMode: false,
      zoomed: false,
      disableZoom: false,
      diagramDataLoaded: false,
      interval: 300 * 1000,
      noEncryptedIdError: false,
      diagramData: null,
      direction: 0,
      period: 0,
      originalTitle: null
    }
  },
  computed: {
    ...mapGetters([
      'storeLng',
      'storeSettings',
      'storeUser'
    ]),
    noSeriesData: function () {
      let noDataExists = false
      if (this.chartOptions && this.chartOptions.series) {
        if (this.chartOptions.series.length === 0) {
          noDataExists = true
        }
      } else {
        noDataExists = true
      }
      return noDataExists
    }
  },
  watch: {
    customChartOptions: {
      handler: function (val, oldVal) {
        if (this.dashboardMode) {
          this.setChartSize()
        }
      },
      deep: true
    }
  },
  methods: {
    checkPublicLang () {
      if (this.$route.query && this.$route.query.lang) {
        const languages = ['de', 'en', 'fr', 'es', 'it']
        const lng = this.$route.query.lang.toLowerCase()
        if (languages.includes(lng)) {
          this.$store.commit('setLng', lng)
          this.$i18n.i18next.changeLanguage(lng)
          this.$vuetify.lang.current = lng
        }
      }
    },
    async getDataByDirection (direction) {
      if (direction === 'original') {
        this.direction = 0
        this.loadDiagramByEncryptedId({
          encrypted_id: this.encryptedId,
          output_type: 'json-series',
          direction: this.direction,
          isZoom: false
        })
      } else {
        if (direction === 'forward') {
          this.direction += 1
        } else if (direction === 'back') {
          this.direction -= 1
        }
        const chart = this.$refs['myChart'].chart
        chart.showLoading()
        await this.getDiagramData({
          encrypted_id: this.encryptedId,
          output_type: 'json-series',
          direction: this.direction,
          isZoom: false
        })
        if (this.dashboardMode) {
          this.setChartSize()
        }
      }
    },
    toggleAllSeries () {
      const chart = this.$refs['myChart'].chart
      for (let i = 0; i < chart.series.length; i++) {
        let serie = chart.series[i]
        serie.setVisible(!serie.visible, false)
      }
      chart.redraw()
    },
    getInterval () {
      if (this.$environment === 'development') {
        this.interval = 10 * 1000
      } else if (this.storeUser && this.storeUser.isProfessional) { // TODO(Karsten): get isProfessional via api if is public
        this.interval = 60 * 1000
      } else {
        this.interval = 300 * 1000
      }
    },
    chartReady (chart) {
      this.chart = chart
    },
    getScreenwidth () {
      let width = window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth
      if (!this.$route.meta.isPublic) {
        width -= 100
      }
      return width - 10
    },
    init () {
      this.id = this.$route.params.id || this.idDiagram

      const match = this.$route.path.match(/^\/#*diagram\/([0-9abcdef]{32})$/)
      if (match) {
        this.id = null
        this.encryptedId = match[1]
      }
    },
    windowResized (e) {
      this.zoomed = false
      const chart = this.$refs['myChart'].chart
      if (chart) {
        this.setSize({
          width: this.getScreenwidth(),
          height: chart.options.chart.height
        })
      }
    },
    setSize (options) {
      const chart = this.$refs['myChart'].chart
      chart.options.xAxis = {
        labels: {
          formatter: function () {
            return this.value.charAt(0)
          }
        }
      }
      chart.setSize(options.width, options.height)

      chart.yAxis.forEach((axis, index) => {
        if (options.width < 500) {
          chart.yAxis[index].update({
            labels: {
              enabled: false
            },
            title: {
              text: ''
            }
          })
        } else {
          chart.yAxis[index].update({
            labels: {
              enabled: true
            },
            title: {
              text: this.yAxisOrig[index]
            }
          })
        }
      })
    },
    setChartSize () {
      let width = 442 // default width for initial dashboard diagram
      let height = 205 // default height for initial dashboard diagram

      const chart = this.$refs['myChart'].chart
      if (this.customChartOptions && this.customChartOptions.width && this.customChartOptions.width > width) {
        width = this.customChartOptions.width
      } else if (chart.containerWidth && chart.containerWidth > width) {
        width = chart.containerWidth
      }

      if (this.customChartOptions && this.customChartOptions.height && this.customChartOptions.height > height) {
        height = this.customChartOptions.height
      } else if (chart.containerHeight && chart.containerHeight > height) {
        height = chart.containerHeight
      }
      width -= 10 // Verhindert das Abschneiden am rechten Rand
      height -= 40 // Buttons für zurück / weiter anzeigen

      this.$nextTick(function () {
        this.setSize({
          width,
          height
        })
      })
    },
    setHighchartsOptions () {
      this.timezoneOffset = 0
      if (this.timezone && momentTz.tz.zone(this.timezone)) {
        this.timezoneOffset = momentTz.tz.zone(this.timezone).utcOffset(momentTz.utc())
      } else {
        console.log('Could not get timezone offset of %s!', this.timezone)
      }
      Highcharts.setOptions({
        time: {
          useUTC: true
        },
        global: {
          useUTC: true
        }
      })
      const chart = this.$refs['myChart'].chart
      chart.showLoading()
    },
    loadDiagramById () {
      let result = null
      return this.$http.get('visualization/diagram/' + this.id).then(response => {
        if (response.data && response.data.encrypted_id) {
          result = response.data.encrypted_id
          this.diagramData = response.data
        }
        return result
      })
    },
    convertAxisTitle (unitId) {
      return unitId.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
        return str.toUpperCase()
      })
    },
    getUnitFromUnitcode (unitCode) {
      let unit = unitCode
      if (Units.units[unitCode]) {
        unit = Units.units[unitCode].unitText
      }
      return unit
    },
    setFormatter () {
      const that = this
      if (this.chartOptions && this.chartOptions.tooltip) {
        this.chartOptions.tooltip.formatter = function () {
          if (this.storeLng) {
            that.$moment.locale(this.storeLng) // TODO(Karsten): get lang via api if is public
          }
          let diff = 0
          if (that.ownership.is_demo) {
            diff = parseInt(that.$moment().format('YYYY')) - 2016
          }
          let tooltip = '<b>REPLACE_BY_DATE</b><br/>' // TODO(Karsten): get dateTimeFormat via api if is public

          this.points.forEach(point => {
            if (point.series.type === 'column') {
              const dt = that.$moment.utc(this.x).add(diff, 'years').format(that.storeSettings.dateFormat)
              tooltip = tooltip.replace('REPLACE_BY_DATE', dt)
            } else {
              const dtf = that.$moment.utc(this.x).add(diff, 'years').format(that.storeSettings.dateTimeFormat)
              tooltip = tooltip.replace('REPLACE_BY_DATE', dtf)
            }
            let value = point.y
            if (point.series.userOptions.unitFamily === 'DateTime') { // system date of controller
              // value = momentTz(parseFloat(value) + 978307200.0, 'X', point.series.timezone).format(that.storeSettings.dateTimeFormat)
            }
            let unit = point.series.tooltipOptions.valueSuffix
            if (point.series.userOptions.rootTypeId === 'DateTime' || point.series.userOptions.rootTypeId === 'Weektime') {
              value = that.$moment.utc(value, 'x').format(that.storeSettings.dateTimeFormat)
            } else if (point.series.tooltipOptions.mode) {
              if (value > 0) {
                unit = '<img src="static/images/icons/relay_on.png" style="height:12px">'
              } else {
                unit = '<img src="static/images/icons/relay_off.png" style="height:12px">'
              }
              value = ''
            } else if (value >= 600 || value <= -600) {
              if (point.series.userOptions.unitFamily === 'Temperature') {
                if (value < -600) {
                  unit += ' <i style="color:red" class="fa fa-flash" aria-hidden="true"></i>'
                } else {
                  unit += ' <i style="color:red" class="fa fa-chain-broken" aria-hidden="true"></i>'
                }
              }
            }
            tooltip += '<i style="color:' + point.series.color + '" class="fa fa-stop" aria-hidden="true"></i> '
            tooltip += ' <span>' + point.series.name + '</span>: '
            tooltip += '<b>' + value + ' ' + unit + '</b>'
            tooltip += '<br/>'
          })
          return tooltip
        }
      }
    },
    getNewTimestamp (data) {
      return parseInt(this.$moment(data).add(5, 'minutes').format('x'))
    },
    getNewValue (data) {
      return Math.round(Math.random() * 100)
    },
    loadDiagram () {
      if (this.id) { // diagram id
        this.loadDiagramById().then(result => {
          this.encryptedId = result
          if (this.encryptedId) {
            this.loadDiagramByEncryptedId()
          } else {
            this.noEncryptedIdError = true
          }
        })
      } else if (this.encryptedId) { // encrypted diagram id
        this.loadDiagramByEncryptedId()
      } else {
        this.$router.push({name: 'Index'})
      }
    },
    loadDiagramByEncryptedId (options) {
      this.loadDiagramOptions(options).then(() => {
        if (this.dashboardMode) {
          this.setChartSize()
        }
        return this.getDiagramData(options)
      }).then(() => {
        this.setFormatter()
        if (this.newDataInterval) {
          clearInterval(this.newDataInterval)
        }
        this.newDataInterval = setInterval(() => {
          // Bitte erst einmal nicht aktivieren
        }, this.interval)
      }).then(() => {
        if (this.dashboardMode) {
          this.setChartSize()
        }
      })
    },
    yAxis (axes) {
      const yAxis = []
      axes.forEach(axis => {
        axis.showEmpty = true
        axis.title.name = axis.title.text
        axis.title.text = this.convertAxisTitle(axis.title.text) + ' [' + this.getUnitFromUnitcode(axis.title.text) + '] '

        yAxis.push(axis)
      })
      return yAxis
    },
    loadDiagramOptions () {
      return this.$http.post('v4/diagram-options', {
        encrypted_id: this.encryptedId
      }).then(response => {
        let options = {}
        if (response.data && typeof response.data === 'object') {
          const width = this.getScreenwidth()
          response.data.yAxis.forEach((axis, index) => {
            let text = response.data.yAxis[index].title.text
            text = text.replace(/(?:^|\.?)([A-Z])/g, function (x, y) { return ' ' + y })

            this.yAxisOrig.push(text)
            if (width < 500 && response.data.yAxis.length > 1) {
              response.data.yAxis[index].visible = false
            }
          })

          options = response.data

          this.period = options.params.period
          this.timezone = options.params.timezone
          this.timezoneOffset = options.params.timezoneOffset
          this.setHighchartsOptions()

          options.xAxis.events = {
            setExtremes: e => {
              if (!this.seriesCached && this.chartOptions.series) {
                this.seriesCached = JSON.parse(JSON.stringify(this.chartOptions.series))
              }
              if (!this.disableZoom) {
                if (e.min === undefined && e.max === undefined) {
                  this.chartOptions.series = JSON.parse(JSON.stringify(this.seriesCached))
                  // reset zoom
                  this.zoomed = false
                } else {
                  this.zoomed = true
                  this.disableZoom = true
                  const chart = this.$refs['myChart'].chart
                  chart.showLoading()

                  this.getDiagramData({
                    encrypted_id: this.encryptedId,
                    start_date_locale: e.min,
                    end_date_locale: e.max,
                    output_type: 'json-series',
                    direction: this.direction,
                    isZoom: true
                  })
                }
              }
            }
          }

          this.ownership = options.params
          options.yAxis = this.yAxis(options.yAxis)

          let height = null
          if (height) {
            options.chart.height = height
          }
          options.chart.width = this.getScreenwidth()
        }
        this.options = options
        if (this.options && this.options.is_demo && this.$environment !== 'development') {
          this.interval = 900 * 1000
        }
        this.chartOptions = options
        return response
      }).catch(error => {
        console.error('loadDiagramOptions', error)
        this.errors.push(this.$t('general.errorLoadData'))
      })
    },
    hashCode (str) {
      str = JSON.stringify(str)
      let hash = 0
      if (str.length === 0) {
        return hash
      }
      for (let i = 0; i < str.length; i++) {
        let char = str.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
      }
      return 'c' + hash
    },
    async getDiagramData (options) {
      this.diagramDataLoaded = false
      let data = options
      if (!options) {
        data = {
          encrypted_id: this.encryptedId,
          output_type: 'json-series',
          // output_type: 'json-series-energy',
          direction: 0,
          isZoom: false
        }
      }
      const hash = this.hashCode(data)
      let response = null
      if (CACHE[hash]) {
        response = CACHE[hash]
      } else {
        response = await this.$http.post('v5/data/diagram', data)
        if (!data.isZoom) {
          CACHE[hash] = response
        }
      }
      try {
        const chart = this.$refs['myChart'].chart

        if (this.options && response.status === 200) {
          if (this.direction !== 0) {
            const subTitle = response.data.params.subtitle
            this.options.subtitle.text = subTitle
          } else {
            if (!this.originalTitle) {
              this.originalTitle = this.options.subtitle.text
            } else {
              this.options.subtitle.text = this.originalTitle
            }
          }
          this.series = response.data.series
          this.series.forEach((serie, index) => {
            this.series[index].type = (this.options.yAxis[this.series[index].yAxis]) ? this.options.yAxis[this.series[index].yAxis].type : 'line'
          })
          this.options.series = this.series
          this.chartOptions = this.options
          this.disableZoom = false
          this.options.yAxis.forEach((axis, index) => {
            if (axis.min || axis.max) {
              if (chart.yAxis && typeof chart.yAxis === 'object') {
                chart.yAxis[index].update({
                  min: axis.min,
                  max: axis.max,
                  floor: axis.min,
                  ceiling: axis.max,
                  endOnTick: false,
                  allowDecimals: false
                }, true)
              }
            }
          })
        }
        chart.hideLoading()
        this.diagramDataLoaded = true
        return response
      } catch (error) {
        console.error('Catch Error', error)
        this.disableZoom = false
        this.errors.push(this.$t('general.errorLoadData'))
      }
    }
  }
}
</script>

<style>
.icon-hover {
  cursor: pointer;
}
.highcharts-loading {
  opacity: 1!important;
}
.highcharts-loading-inner {
  display: block;
}
.highcharts-loading-inner,
.highcharts-loading-inner:before,
.highcharts-loading-inner:after {
  background: #d4d4d1;
  -webkit-animation: chartLoading 1s infinite ease-in-out;
  animation: chartLoading 1s infinite ease-in-out;
  width: 1em;
  height: 4em;
}
.highcharts-loading-inner {
  color: #d4d4d1;
  text-indent: -9999em;
  margin: 0 auto;
  top: 50%!important;
  position: relative;
  font-size: 12px;
  -webkit-transform: translate3d(-50%, -50%, 0);
  -ms-transform: translate3d(-50%, -50%, 0);
  transform: translate3d(-50%, -50%, 0);
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}
.highcharts-loading-inner:before,
.highcharts-loading-inner:after {
  position: absolute;
  top: 0;
  content: '';
}
.highcharts-loading-inner:before {
  left: -1.5em;
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}
.highcharts-loading-inner:after {
  left: 1.5em;
}
@-webkit-keyframes chartLoading {
  0%,
  80%,
  100% {
    box-shadow: 0 0;
    height: 4em;
  }
  40% {
    box-shadow: 0 -2em;
    height: 5em;
  }
}
@keyframes chartLoading {
  0%,
  80%,
  100% {
    box-shadow: 0 0;
    height: 4em;
  }
  40% {
    box-shadow: 0 -2em;
    height: 5em;
  }
}
</style>



// WEBPACK FOOTER //
// src/client/views/visualization/DiagramShow.vue